import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ButtonSubmit } from "@/components/blocks/button-submit";
import { useTranslation } from "@/providers/language-provider";

const formSchema = z
  .object({
    name: z
      .string()
      .min(4, { message: "Name must be at least 4 characters long." }),
    email: z
      .string()
      .email({ message: "Please enter a valid email address." })
      .min(5, { message: "Email must be at least 5 characters long." }),
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long." })
      .max(32, {
        message: "Password must be no more than 32 characters long.",
      }),
    passwordConfirm: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long." })
      .max(32, {
        message: "Password must be no more than 32 characters long.",
      }),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Passwords do not match.",
    path: ["passwordConfirm"], // Specify the path for the error message
  });

export function AuthSignup({ onRegister }: { onRegister: any }) {
  const { t } = useTranslation();
  // const [picture, setPicture] = useState(
  //   "/images/anonymous-icon.jpg",
  // );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      passwordConfirm: "",
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    await onRegister(data.name, data.email, data.password);
    setIsSubmitting(false);
  }

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-2"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Enter your name."
                    autoComplete="username"
                    required
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter your e-mail."
                    autoComplete="email"
                    required
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password."
                    autoComplete="current-password"
                    required
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Password is 8~32 characters long.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Confirm password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password."
                    autoComplete="current-password"
                    required
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Password is 8~32 characters long.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="w-full space-y-2 pt-3">
            <ButtonSubmit
              className="w-full"
              type="submit"
              isSubmitting={isSubmitting}
            >
              {t("Register")}
            </ButtonSubmit>
          </div>
          {/* <div className="space-y-1">
            <Label htmlFor="picture">Upload Your Picture</Label>
            <img
              src={picture}
              alt="Avatar"
              className="w-36 h-36 rounded-full object-cover mx-auto"
            />
            <Input
              id="picture"
              type="file"
              accept="image/*"
              // onChange={(e) => postDetails(e.target.files[0])}
              // isDisabled={loading}
            />
          </div> */}
        </form>
      </Form>
    </>
  );
}
